import * as THREE from 'three';

export type BrushKind = 'box' | 'ramp';

export interface BrushFaceMaterial {
  color: number;
  roughness: number;
  metalness: number;
  emissive?: number;
  emissiveIntensity?: number;
}

/**
 * Axis-aligned brush used for both collision and graybox rendering.
 * Ramps are boxes with a cut plane: walkable surface slopes along local +X.
 */
export interface Brush {
  id: string;
  kind: BrushKind;
  /** World-space center */
  center: THREE.Vector3;
  /** Full size (width, height, depth) */
  size: THREE.Vector3;
  /**
   * Yaw (radians) — same convention as THREE.Object3D.rotation.y
   * (Three.js: +X rotates toward −Z). Ramps rise along local +X after yaw.
   */
  yaw: number;
  material?: BrushFaceMaterial;
  /** If true, skipped for collision (visual only) */
  noCollision?: boolean;
  /** If true, collision only — no rendered mesh */
  invisible?: boolean;
  /** Splash (grenade / rocket) can break this brush into chunks. */
  explodable?: boolean;
}

export interface Aabb {
  min: THREE.Vector3;
  max: THREE.Vector3;
}

export function brushAabb(brush: Brush, out: Aabb): Aabb {
  const hx = brush.size.x * 0.5;
  const hy = brush.size.y * 0.5;
  const hz = brush.size.z * 0.5;

  if (Math.abs(brush.yaw) < 1e-6) {
    out.min.set(brush.center.x - hx, brush.center.y - hy, brush.center.z - hz);
    out.max.set(brush.center.x + hx, brush.center.y + hy, brush.center.z + hz);
    return out;
  }

  // Conservative AABB — Three.js rotation.y: x' = c*x + s*z, z' = -s*x + c*z
  const c = Math.cos(brush.yaw);
  const s = Math.sin(brush.yaw);
  const corners = [
    [hx, hz],
    [hx, -hz],
    [-hx, hz],
    [-hx, -hz],
  ];
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const [lx, lz] of corners) {
    const wx = brush.center.x + lx * c + lz * s;
    const wz = brush.center.z - lx * s + lz * c;
    minX = Math.min(minX, wx);
    maxX = Math.max(maxX, wx);
    minZ = Math.min(minZ, wz);
    maxZ = Math.max(maxZ, wz);
  }
  out.min.set(minX, brush.center.y - hy, minZ);
  out.max.set(maxX, brush.center.y + hy, maxZ);
  return out;
}

/** Height of solid geometry at world XZ for a brush, or -Infinity if outside. */
export function brushHeightAt(brush: Brush, x: number, z: number): number {
  const aabb = brushAabb(brush, _aabb);
  if (x < aabb.min.x || x > aabb.max.x || z < aabb.min.z || z > aabb.max.z) {
    return -Infinity;
  }

  if (brush.kind === 'ramp') {
    // Inverse of Three.js rotation.y — high at local +X matches the mesh
    const c = Math.cos(brush.yaw);
    const s = Math.sin(brush.yaw);
    const dx = x - brush.center.x;
    const dz = z - brush.center.z;
    const localX = dx * c - dz * s;
    const localZ = dx * s + dz * c;
    const hx = brush.size.x * 0.5;
    const hz = brush.size.z * 0.5;
    const eps = 1e-4;
    if (localZ < -hz - eps || localZ > hz + eps || localX < -hx - eps || localX > hx + eps) {
      return -Infinity;
    }
    const t = clamp01((localX + hx) / brush.size.x);
    const y0 = brush.center.y - brush.size.y * 0.5;
    return y0 + t * brush.size.y;
  }

  return aabb.max.y;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

const _aabb: Aabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };
